import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { HubService, StorageHubPayload, StorageHub, LookupData } from '../../services/hub.service';

@Component({
  selector: 'app-add-storage-hub-modal',
  templateUrl: './add-storage-hub-modal.component.html',
  styleUrls: ['./add-storage-hub-modal.component.scss']
})
export class AddStorageHubModalComponent implements OnInit, OnChanges {
  @Input() isVisible = false;
  @Input() editHub: StorageHub | null = null;
  @Input() duplicateSource: StorageHub | null = null;
  @Input() existingHubs: StorageHub[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ name: string }>();

  isSaving = false;
  errorMessage: string | null = null;
  locationTypes: LookupData[] = [];
  isLoadingTypes = false;
  isEditMode = false;
  isDuplicateMode = false;
  errors: { latitude?: string; longitude?: string } = {};

  form: StorageHubPayload = {
    location_name: '',
    location_type: '',
    location_desc: '',
    location_latitude_value: null as unknown as number,
    location_longitude_value: null as unknown as number,
    is_direct_update_allowed: true,
    is_transfer_allowed: true,
    is_mat_allocation_source: true,
    is_location_active: true
  };

  constructor(private hubService: HubService) {}

  ngOnInit(): void {
    this.loadLocationTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editHub'] && changes['editHub'].currentValue) {
      this.isEditMode = true;
      this.isDuplicateMode = false;
      const hub = changes['editHub'].currentValue as StorageHub;
      this.form = {
        location_name: hub.LocationName || '',
        location_type: hub.LocationType || '',
        location_desc: hub.LocationDesc || '',
        location_latitude_value: hub.LocationLatitudeValue || null as unknown as number,
        location_longitude_value: hub.LocationLongitudeValue || null as unknown as number,
        is_direct_update_allowed: hub.IsDirectUpdateAllowed === 'Y',
        is_transfer_allowed: hub.IsTransferAllowed === 'Y',
        is_mat_allocation_source: hub.IsMatAllocationSource === 'Y',
        is_location_active: hub.IsLocationActive === 'Y'
      };
    } else if (changes['duplicateSource'] && changes['duplicateSource'].currentValue) {
      this.isEditMode = false;
      this.isDuplicateMode = true;
      const hub = changes['duplicateSource'].currentValue as StorageHub;
      this.form = {
        location_name: 'Copy of ' + (hub.LocationName || ''),
        location_type: hub.LocationType || '',
        location_desc: hub.LocationDesc || '',
        location_latitude_value: hub.LocationLatitudeValue || null as unknown as number,
        location_longitude_value: hub.LocationLongitudeValue || null as unknown as number,
        is_direct_update_allowed: hub.IsDirectUpdateAllowed === 'Y',
        is_transfer_allowed: hub.IsTransferAllowed === 'Y',
        is_mat_allocation_source: hub.IsMatAllocationSource === 'Y',
        is_location_active: hub.IsLocationActive === 'Y'
      };
    } else if (changes['isVisible'] && !changes['isVisible'].currentValue) {
      // Reset form when modal closes
      this.resetForm();
    }
  }

  resetForm(): void {
    this.isEditMode = false;
    this.isDuplicateMode = false;
    this.form = {
      location_name: '',
      location_type: '',
      location_desc: '',
      location_latitude_value: null as unknown as number,
      location_longitude_value: null as unknown as number,
      is_direct_update_allowed: true,
      is_transfer_allowed: true,
      is_mat_allocation_source: true,
      is_location_active: true
    };
    this.errorMessage = null;
    this.errors = {};
  }

  loadLocationTypes(): void {
    this.isLoadingTypes = true;
    this.hubService.getLookupData('Location_Type').subscribe({
      next: (data) => {
        this.locationTypes = data;
        this.isLoadingTypes = false;
      },
      error: (err) => {
        console.error('Failed to load location types', err);
        // Fallback to default options if API fails
        this.locationTypes = [
          { CodeName: 'HUB', CodeDesc: 'HUB' },
          { CodeName: 'WAREHOUSE', CodeDesc: 'WAREHOUSE' },
          { CodeName: 'SITE', CodeDesc: 'SITE' }
        ];
        this.isLoadingTypes = false;
      }
    });
  }

  onClose() {
    if (this.isSaving) return;
    this.close.emit();
  }

  saveHub() {
    this.errorMessage = null;
    if (!this.form.location_name || this.form.location_name.trim().length === 0) {
      this.errorMessage = 'Location name is required';
      return;
    }

    if (!this.form.location_type || this.form.location_type.trim().length === 0) {
      this.errorMessage = 'Location type is required';
      return;
    }

    if (!this.form.location_desc || this.form.location_desc.trim().length === 0) {
      this.errorMessage = 'Description is required';
      return;
    }

    // Duplicate check: location name + type must be unique (exclude self when editing)
    const nameNorm = this.form.location_name.trim().toLowerCase();
    const typeNorm = (this.form.location_type || '').trim().toLowerCase();
    const duplicate = this.existingHubs.find(h =>
      h.LocationName?.trim().toLowerCase() === nameNorm &&
      (h.LocationType || '').trim().toLowerCase() === typeNorm &&
      (!this.isEditMode || h.LocationId !== this.editHub?.LocationId)
    );
    if (duplicate) {
      this.errorMessage = `A storage hub with the name "${this.form.location_name.trim()}" and type "${this.form.location_type}" already exists.`;
      return;
    }

    // Validate latitude — required, must be between -90 and 90
    this.errors = {};
    const latVal = this.form.location_latitude_value;
    if (latVal === null || latVal === undefined || (latVal as any) === '') {
      this.errors.latitude = 'Latitude is required';
    } else {
      const lat = Number(latVal);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        this.errors.latitude = 'Latitude must be a number between -90 and 90';
      } else {
        this.form.location_latitude_value = lat;
      }
    }

    // Validate longitude — required, must be between -180 and 180
    const lonVal = this.form.location_longitude_value;
    if (lonVal === null || lonVal === undefined || (lonVal as any) === '') {
      this.errors.longitude = 'Longitude is required';
    } else {
      const lon = Number(lonVal);
      if (isNaN(lon) || lon < -180 || lon > 180) {
        this.errors.longitude = 'Longitude must be a number between -180 and 180';
      } else {
        this.form.location_longitude_value = lon;
      }
    }

    if (this.errors.latitude || this.errors.longitude) {
      return;
    }

    this.isSaving = true;
    
    if (this.isEditMode && this.editHub?.LocationId) {
      // Update existing hub - include LocationId in payload
      const updatePayload = {
        ...this.form,
        location_id: this.editHub.LocationId.toString()
      };
      this.hubService.updateStorageHub(updatePayload).subscribe({
        next: () => {
          this.isSaving = false;
          this.saved.emit({ name: this.form.location_name });
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = 'Failed to update storage hub';
          console.error(err);
        }
      });
    } else {
      // Add new hub
      this.hubService.addStorageHub(this.form).subscribe({
        next: () => {
          this.isSaving = false;
          this.saved.emit({ name: this.form.location_name });
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = 'Failed to add storage hub';
          console.error(err);
        }
      });
    }
  }
}
